"use client";

import { useSyncExternalStore, type ReactNode } from "react";
import { NavButton } from "@repo/ui/nav-button";

import { ListingCardContainer } from "@/components/cards/listing-card/listing-card-container.component";

export const CAMPAIGN_PARAM = "campaign";

// The URL is the source of truth for the active campaign, read as an external
// store: the server snapshot is empty (the page is statically rendered), so
// deep links like ?campaign=<slug> apply right after hydration without a
// mismatch. Chip clicks write the URL via history.replaceState — no
// navigation, so the page stays static — and notify subscribers manually,
// since replaceState fires no event.
const urlListeners = new Set<() => void>();

function subscribeToUrl(listener: () => void) {
  window.addEventListener("popstate", listener);
  urlListeners.add(listener);
  return () => {
    window.removeEventListener("popstate", listener);
    urlListeners.delete(listener);
  };
}

const getSearch = () => window.location.search;
const getServerSearch = () => "";

export interface GridCampaign {
  slug: string;
  label: string;
}

export interface GridCard {
  key: string;
  campaign: string | null;
  node: ReactNode;
}

export function PromotionsGridClient({
  cards,
  campaigns,
  showFilter,
}: {
  cards: GridCard[];
  campaigns: GridCampaign[];
  showFilter: boolean;
}) {
  const search = useSyncExternalStore(
    subscribeToUrl,
    getSearch,
    getServerSearch,
  );
  const filterVisible = showFilter && campaigns.length > 0;
  const requested = new URLSearchParams(search).get(CAMPAIGN_PARAM);
  const active =
    filterVisible && requested && campaigns.some((c) => c.slug === requested)
      ? requested
      : null;

  const selectCampaign = (slug: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (slug) params.set(CAMPAIGN_PARAM, slug);
    else params.delete(CAMPAIGN_PARAM);
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}`,
    );
    for (const listener of urlListeners) listener();
  };

  const visible = active ? cards.filter((c) => c.campaign === active) : cards;

  return (
    <>
      {filterVisible && (
        <div className="container-grid mb-6">
          <div className="col-span-4 flex flex-wrap gap-2 tablet:col-span-8 desktop:col-span-12">
            <NavButton
              variant="item"
              active={active === null}
              onClick={() => selectCampaign(null)}
            >
              All
            </NavButton>
            {campaigns.map((c) => (
              <NavButton
                key={c.slug}
                variant="item"
                active={active === c.slug}
                onClick={() => selectCampaign(c.slug)}
              >
                {c.label}
              </NavButton>
            ))}
          </div>
        </div>
      )}
      {visible.length === 0 ? (
        <div className="container-grid">
          <p className="col-span-4 rounded-md bg-surface-base-secondary p-10 text-center typography-component-md-regular text-text-secondary tablet:col-span-8 desktop:col-span-12">
            No active promotions right now.
          </p>
        </div>
      ) : (
        <ListingCardContainer columns={3} rowGap="lg" carouselOnMobile={false}>
          {visible.map((c) => c.node)}
        </ListingCardContainer>
      )}
    </>
  );
}
